// Reine, framework-freie Helfer für den `POST /api/note-grips`-Konsumenten (NoteGripInspector.vue).
// Bewusst OHNE Vue/OSMD, damit sie – wie die MusicXML-DOM-Helfer – direkt unit-testbar sind
// (siehe src/__tests__/noteGripInspector.spec.ts).

import {findPartForNote, getParts, parseMusicXml} from '@/composables/musicXmlDom';
import type {NoteInfo} from '@/composables/musicXmlDom';
import {gripButtonId, type Grip, type NoteGripsRequest} from './gripTypes';

/**
 * 0-basierter Part-Index (== music21 `score.parts`-Index), den `note_grips`' `part_index` erwartet.
 *
 * **Aufgelöst über `staffIndex`, NICHT über `partId` (kritisch – nicht rückbauen).** Beide Hälften
 * des Round-Trips müssen denselben Schlüssel benutzen: der Schreib-Pfad
 * (`applyGripToNoteInMusicXml` → `findPartAndMeasureNote` → `findPartForNote(doc, staffIndex)`)
 * adressiert über `staffIndex`. OSMDs `partId` (`ParentInstrument.IdString`) ist dagegen
 * unzuverlässig – mal leer, mal für mehrere Parts identisch –, und der frühere
 * `findIndex(id === partId)`-Zweig fiel dann still auf 0 zurück bzw. traf den falschen Part: der
 * Inspector zeigte Griff und Alternativen einer Note aus einem ANDEREN System als das, in das
 * anschließend geschrieben wurde. `findPartForNote` akkumuliert `<staves>` und deckt damit auch
 * Grand-Staff-Parts ab. Parse-Fehler → 0 (defensiv, nie werfen).
 */
export function partIndexForNote(musicXml: string, staffIndex: number | undefined): number {
    if (staffIndex === undefined || staffIndex < 0) return 0;
    try {
        const doc = parseMusicXml(musicXml);
        const part = findPartForNote(doc, staffIndex);
        if (!part) return 0;
        const idx = getParts(doc).indexOf(part);
        return idx >= 0 ? idx : 0;
    } catch {
        return 0;
    }
}

/**
 * Baut den `note_grips`-Request aus einer angeklickten Ausgabe-Note. Pausen und „keine Auswahl"
 * liefern `null` (→ der Konsument fragt dann nicht an). `measure_number`/`voice_note_index`/`voice`
 * mappen 1:1 aus `NoteInfo`; ein vorhandener `measureIndex` wird ohne Umrechnung als
 * `measure_index` gesendet, `part_index` wird über den `staffIndex` aus dem Dokument aufgelöst
 * (derselbe Schlüssel, den auch der Schreib-Pfad benutzt – siehe `partIndexForNote`).
 */
export function buildNoteGripsRequest(musicXml: string, note: NoteInfo | null | undefined): NoteGripsRequest | null {
    if (!note || note.isRest) return null;
    return {
        music_xml: musicXml,
        measure_number: note.measureNumber,
        ...(note.measureIndex === undefined ? {} : {measure_index: note.measureIndex}),
        voice_note_index: note.voiceNoteIndex,
        voice: note.voice,
        part_index: partIndexForNote(musicXml, note.staffIndex),
    };
}

/**
 * Abbildung Highlight-ID (`${row-1}-${number-1}`) → klingender Ton der Knöpfe eines/mehrerer Griffe.
 * Speist die `getLabel`-Callback von `InstrumentBody`, damit hervorgehobene Knöpfe ihren Ton zeigen.
 */
export function gripButtonNoteMap(...grips: (Grip | null | undefined)[]): Map<string, string> {
    const map = new Map<string, string>();
    for (const grip of grips) {
        if (!grip) continue;
        for (const button of grip.buttons) map.set(gripButtonId(button), button.note);
    }
    return map;
}
